"use client"

import { memo, useCallback, useEffect, useRef, useState } from "react"
import { EditorContent, EditorContext, useEditor, type Editor } from "@tiptap/react"

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit"
import { TaskItem, TaskList } from "@tiptap/extension-list"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { Highlight } from "@tiptap/extension-highlight"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { TableKit } from "@tiptap/extension-table"
import { Selection } from "@tiptap/extensions"

// --- UI Primitives ---
import { Button } from "../../tiptap-ui-primitive/button"
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "../../tiptap-ui-primitive/toolbar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../tiptap-ui-primitive/dropdown-menu"

// --- Tiptap Node ---
import { ImageUploadNode } from "../../tiptap-node/image-upload-node/image-upload-node-extension"
import { AttachmentImage } from "../../tiptap-node/image-node/attachment-image-extension"
import { HorizontalRule } from "../../tiptap-node/horizontal-rule-node/horizontal-rule-node-extension"
import "../../tiptap-node/blockquote-node/blockquote-node.scss"
import "../../tiptap-node/code-block-node/code-block-node.scss"
import "../../tiptap-node/horizontal-rule-node/horizontal-rule-node.scss"
import "../../tiptap-node/list-node/list-node.scss"
import "../../tiptap-node/image-node/image-node.scss"
import "../../tiptap-node/heading-node/heading-node.scss"
import "../../tiptap-node/paragraph-node/paragraph-node.scss"

// --- Tiptap UI ---
import { ListButton } from "../../tiptap-ui/list-button"
import {
  ColorHighlightPopover,
  ColorHighlightPopoverContent,
  ColorHighlightPopoverButton,
} from "../../tiptap-ui/color-highlight-popover"
import {
  LinkPopover,
  LinkContent,
  LinkButton,
} from "../../tiptap-ui/link-popover"
import { MarkButton } from "../../tiptap-ui/mark-button"

// --- Icons ---
import { ArrowLeftIcon } from "../../tiptap-icons/arrow-left-icon"
import { ChevronDownIcon } from "../../tiptap-icons/chevron-down-icon"
import { HighlighterIcon } from "../../tiptap-icons/highlighter-icon"
import { ImagePlusIcon } from "../../tiptap-icons/image-plus-icon"
import { LinkIcon } from "../../tiptap-icons/link-icon"

// --- Hooks ---
import { useIsBreakpoint } from "../../../hooks/use-is-breakpoint"
import { useWindowSize } from "../../../hooks/use-window-size"
import { useCursorVisibility } from "../../../hooks/use-cursor-visibility"

// --- Lib ---
import { handleImageUpload, MAX_FILE_SIZE } from "../../../lib/tiptap-utils"
import { getUnsupportedAttachmentMessage } from "../../../../../utils/attachmentValidation"
import {
  getClipboardImageFilesForUpload,
  getImageFiles,
} from "./clipboard"
import { Callout } from "./callout-extension"
import { FontSize } from "./font-size-extension"
import { tabSeparatedTableToHtml } from "../../../../../utils/tableClipboard"
import { snapshotFiles } from "../../../../../utils/clipboardFiles"

// --- Styles ---
import "../../tiptap-templates/simple/simple-editor.scss"

const ExternalHighlight = Highlight.extend({
  parseHTML() {
    return [
      ...(this.parent?.() || []),
      {
        style: "background-color",
        consuming: false,
        getAttrs: (color) => {
          const normalizedColor = String(color).trim().toLowerCase()
          return normalizedColor && normalizedColor !== "transparent"
            ? { color: String(color).trim() }
            : false
        },
      },
    ]
  },
})

const TEXT_FORMATS = [
  { label: "Обычный текст", level: null },
  { label: "Заголовок 1", level: 1 },
  { label: "Заголовок 2", level: 2 },
  { label: "Заголовок 3", level: 3 },
] as const

const TextFormatDropdown = ({ editor }: { editor: Editor | null }) => {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    if (!editor) return

    const updateFormat = () => forceUpdate((value) => value + 1)
    editor.on("selectionUpdate", updateFormat)
    editor.on("transaction", updateFormat)

    return () => {
      editor.off("selectionUpdate", updateFormat)
      editor.off("transaction", updateFormat)
    }
  }, [editor])

  const activeFormat = TEXT_FORMATS.find(({ level }) => (
    level == null
      ? editor?.isActive("paragraph")
      : editor?.isActive("heading", { level })
  )) || TEXT_FORMATS[0]

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          className="simple-editor-format-button"
          disabled={!editor}
          showTooltip={false}
          type="button"
          variant="ghost"
        >
          <span className="simple-editor-format-label">{activeFormat.label}</span>
          <ChevronDownIcon className="tiptap-button-dropdown-small" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {TEXT_FORMATS.map((format) => (
          <DropdownMenuItem
            key={format.label}
            onSelect={() => {
              if (!editor) return

              if (format.level == null) {
                editor.chain().focus().setParagraph().run()
              } else {
                editor.chain().focus().setHeading({ level: format.level }).run()
              }
            }}
          >
            {format.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const MainToolbarContent = ({
  editor,
  onImageClick,
  onHighlighterClick,
  onLinkClick,
  isMobile,
}: {
  editor: Editor | null
  onImageClick: () => void
  onHighlighterClick: () => void
  onLinkClick: () => void
  isMobile: boolean
}) => {
  return (
    <>
      <ToolbarGroup>
        <TextFormatDropdown editor={editor} />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        <ListButton type="bulletList" />
        <ListButton type="orderedList" />
        <Button
          aria-label="Вставить таблицу"
          disabled={!editor}
          onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          type="button"
          variant="ghost"
        >
          <svg className="tiptap-button-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="1" stroke="currentColor" strokeWidth="1.7" />
            <path d="M3 9.5h18M3 14.5h18M9 4v16M15 4v16" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </Button>
        <Button
          aria-label="Добавить изображение"
          onClick={onImageClick}
          tooltip="Добавить изображение"
          type="button"
          variant="ghost"
        >
          <ImagePlusIcon className="tiptap-button-icon" />
        </Button>
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>
    </>
  )
}

const MobileToolbarContent = ({
  type,
  onBack,
}: {
  type: "highlighter" | "link"
  onBack: () => void
}) => (
  <>
    <ToolbarGroup>
      <Button variant="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? (
      <ColorHighlightPopoverContent />
    ) : (
      <LinkContent />
    )}
  </>
)


export interface InlineImageUploadResult {
  src: string
  attachmentId?: number | string
  onResolve?: () => void
  onDiscard?: () => void | Promise<void>
}

export type InlineImageUploadHandler = (
  file: File,
  pendingId: string,
) => Promise<InlineImageUploadResult | null>

interface SimpleEditorProps {
  autoFocus?: boolean
  editable?: boolean
  saveEditorData?: (html: string) => void | Promise<void>
  showToolbar?: boolean
  onChange?: (html: string) => void
  onInlineImageUpload?: InlineImageUploadHandler
  onInlineImageError?: (error: Error) => void
  html?: string
}

const createPendingImageId = () => (
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `inline-image-${Date.now()}-${Math.random().toString(36).slice(2)}`
)

export const SimpleEditor = memo(function SimpleEditor({
  autoFocus = false,
  editable = true,
  saveEditorData = () => {},
  showToolbar = true,
  onChange = () => {},
  onInlineImageUpload,
  onInlineImageError,
  html,
}: SimpleEditorProps) {
  const isMobile = useIsBreakpoint()
  const { height } = useWindowSize()
  const [mobileView, setMobileView] = useState<"main" | "highlighter" | "link">(
    "main"
  )
  const toolbarRef = useRef<HTMLDivElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const uploadHandlerRef = useRef(onInlineImageUpload)
  const uploadErrorHandlerRef = useRef(onInlineImageError)
  const previewUrlsRef = useRef(new Set<string>())
  const editorInstanceRef = useRef<any>(null)

  uploadHandlerRef.current = onInlineImageUpload
  uploadErrorHandlerRef.current = onInlineImageError

  const releasePreviewUrl = useCallback((url: string) => {
    if (!previewUrlsRef.current.has(url)) return
    previewUrlsRef.current.delete(url)
    URL.revokeObjectURL(url)
  }, [])

  const uploadInlineImages = useCallback((editorInstance: any, files: File[], position: number) => {
    if (!files.length || !uploadHandlerRef.current) return false

    const validationError = getUnsupportedAttachmentMessage(files)
    const oversizedFile = files.find((file) => file.size > MAX_FILE_SIZE)

    if (validationError || oversizedFile) {
      const message = validationError || `File size exceeds maximum allowed (${MAX_FILE_SIZE / 1024 / 1024}MB)`
      uploadErrorHandlerRef.current?.(new Error(message))
      return true
    }

    const pendingImages = files.map((file) => {
      const pendingId = createPendingImageId()
      const previewUrl = URL.createObjectURL(file)
      previewUrlsRef.current.add(previewUrl)

      return { file, pendingId, previewUrl }
    })

    editorInstance
      .chain()
      .focus()
      .insertContentAt(position, pendingImages.map(({ file, pendingId, previewUrl }) => ({
        type: "image",
        attrs: {
          src: previewUrl,
          alt: file.name,
          title: file.name,
          pendingId,
        },
      })))
      .run()

    pendingImages.forEach(({ file, pendingId, previewUrl }) => {
      let uploadedResult: InlineImageUploadResult | null = null
      let discarded = false
      const discardUpload = async () => {
        if (discarded || !uploadedResult?.onDiscard) return
        discarded = true
        await uploadedResult.onDiscard()
      }

      void uploadHandlerRef.current?.(file, pendingId)
        .then(async (result) => {
          if (!result) return
          uploadedResult = result

          if (editorInstance.isDestroyed) {
            await discardUpload()
            releasePreviewUrl(previewUrl)
            return
          }

          let imagePosition: number | null = null
          let imageNode: any = null
          editorInstance.state.doc.descendants((node: any, nodePosition: number) => {
            if (node.type.name === "image" && node.attrs.pendingId === pendingId) {
              imagePosition = nodePosition
              imageNode = node
              return false
            }
            return true
          })

          if (imagePosition !== null && imageNode) {
            const { onResolve, onDiscard: _onDiscard, ...imageAttrs } = result
            const transaction = editorInstance.state.tr.setNodeMarkup(imagePosition, undefined, {
              ...imageNode.attrs,
              ...imageAttrs,
              pendingId: null,
            })
            editorInstance.view.dispatch(transaction)
            onResolve?.()
          } else {
            await discardUpload()
          }

          releasePreviewUrl(previewUrl)
        })
        .catch(async (error) => {
          let imagePosition: number | null = null
          let imageSize = 0
          if (!editorInstance.isDestroyed) {
            editorInstance.state.doc.descendants((node: any, nodePosition: number) => {
              if (node.type.name === "image" && node.attrs.pendingId === pendingId) {
                imagePosition = nodePosition
                imageSize = node.nodeSize
                return false
              }
              return true
            })
          }

          if (imagePosition !== null && !editorInstance.isDestroyed) {
            editorInstance.view.dispatch(
              editorInstance.state.tr.delete(imagePosition, imagePosition + imageSize),
            )
          }

          try {
            await discardUpload()
          } catch (discardError) {
            console.error("Failed to discard unused inline image:", discardError)
          }

          releasePreviewUrl(previewUrl)
          uploadErrorHandlerRef.current?.(
            error instanceof Error ? error : new Error("Failed to upload inline image"),
          )
        })
    })

    return true
  }, [releasePreviewUrl])

  // Clipboard/dropped files may be backed by a temp file the source app
  // removes soon after (Telegram "Copy image"), which later fails the upload
  // with ERR_ACCESS_DENIED. Copy them into memory first, then insert.
  const insertSnapshotInlineImages = useCallback((editorInstance: any, files: File[], getPosition: () => number) => {
    if (!files.length || !uploadHandlerRef.current) return false

    snapshotFiles(files)
      .then((snapshots) => {
        if (editorInstance.isDestroyed) return
        const docSize = editorInstance.state.doc.content.size
        uploadInlineImages(editorInstance, snapshots, Math.min(getPosition(), docSize))
      })
      .catch((error) => {
        console.error("Failed to read pasted image:", error)
        uploadErrorHandlerRef.current?.(new Error("Failed to read pasted image"))
      })

    return true
  }, [uploadInlineImages])

  const editor = useEditor({
    immediatelyRender: false,
    editable,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
        class: "simple-editor",
      },
      handlePaste: (view, event) => {
        const clipboardHtml = event.clipboardData?.getData("text/html") || ""
        const clipboardText = event.clipboardData?.getData("text/plain") || ""
        if (!/<table[\s>]/i.test(clipboardHtml)) {
          const tableHtml = tabSeparatedTableToHtml(clipboardText)
          if (tableHtml && editorInstanceRef.current) {
            const { from, to } = view.state.selection
            editorInstanceRef.current.chain().focus().insertContentAt({ from, to }, tableHtml).run()
            return true
          }
        }

        // Let ProseMirror parse the HTML clipboard flavor whenever the paste
        // contains text. Some external editors also expose embedded images as
        // files; consuming that event here would discard all rich-text markup.
        const files = getClipboardImageFilesForUpload(event.clipboardData)
        if (!files.length) return false

        const editorInstance = editorInstanceRef.current
        return editorInstance
          ? insertSnapshotInlineImages(editorInstance, files, () => editorInstance.state.selection.from)
          : false
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false

        const files = getImageFiles(event.dataTransfer?.files || null)
        if (!files.length) return false

        const position = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
          ?? view.state.selection.from
        return editorInstanceRef.current
          ? insertSnapshotInlineImages(editorInstanceRef.current, files, () => position)
          : false
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TableKit,
      ExternalHighlight.configure({ multicolor: true }),
      FontSize,
      Callout,
      AttachmentImage,
      Typography,
      Superscript,
      Subscript,
      Selection,
      ImageUploadNode.configure({
        accept: "image/*",
        maxSize: MAX_FILE_SIZE,
        limit: 3,
        upload: handleImageUpload,
        onError: (error) => console.error("Upload failed:", error),
      }),
    ],
    content: html || "",
    onUpdate: ({ editor }) => {
      onChange(editor.isEmpty ? "" : editor.getHTML())
    },
    onBlur: async ({ editor }) => {
      const html = editor.isEmpty ? "" : editor.getHTML()

      try {
        await saveEditorData(html)
      } catch (error) {
        console.error("Ошибка при сохранении editor data:", error)
      }
    },
  })

  editorInstanceRef.current = editor

  useEffect(() => () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    previewUrlsRef.current.clear()
  }, [])

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  })

  useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main")
    }
  }, [isMobile, mobileView])

  useEffect(() => {
    if (!editor) return

    const nextHtml = html || ""
    const currentHtml = editor.isEmpty ? "" : editor.getHTML()

    if (currentHtml !== nextHtml) {
      editor.commands.setContent(nextHtml, { emitUpdate: false })
    }
  }, [editor, html])

  useEffect(() => {
    if (!editor) return

    editor.setEditable(editable)

    if (editable && autoFocus) {
      window.requestAnimationFrame(() => editor.commands.focus("end"))
    }
  }, [autoFocus, editable, editor])

  return (
    <div className={`simple-editor-wrapper ${editable ? '' : 'simple-editor-wrapper--readonly'}`}>
      {editable ? (
        <input
          ref={imageInputRef}
          accept="image/jpeg,image/png"
          className="simple-editor-image-input"
          multiple
          onChange={(event) => {
            const files = getImageFiles(event.target.files)
            if (editor) {
              uploadInlineImages(editor, files, editor.state.selection.from)
            }
            event.target.value = ""
          }}
          type="file"
        />
      ) : null}
      <EditorContext.Provider value={{ editor }}>
        {showToolbar ? (
          <Toolbar
            ref={toolbarRef}
            style={{
              ...(isMobile
                ? {
                    bottom: `calc(100% - ${height - rect.y}px)`,
                  }
                : {}),
            }}
          >
            {mobileView === "main" ? (
              <MainToolbarContent
                editor={editor}
                onImageClick={() => imageInputRef.current?.click()}
                onHighlighterClick={() => setMobileView("highlighter")}
                onLinkClick={() => setMobileView("link")}
                isMobile={isMobile}
              />
            ) : (
              <MobileToolbarContent
                type={mobileView === "highlighter" ? "highlighter" : "link"}
                onBack={() => setMobileView("main")}
              />
            )}
          </Toolbar>
        ) : null}

        <EditorContent
          editor={editor}
          role="presentation"
          className="simple-editor-content"
        />
      </EditorContext.Provider>
    </div>
  )
})
